import { useState } from 'react'
import { CheckCircle2, KeyRound } from 'lucide-react'
import { apiPost } from '../lib/api'
import {
  IdPFormFields,
  buildIdPPayload,
  computeCallbackURIs,
  emptyIdPForm,
  type IdPFormState,
  PRESET_KEYS,
} from '../components/IdPForm'
import { Button, Card, FormField, Input } from '../components/ui'

const tenantHost = typeof window !== 'undefined' ? window.location.host : ''

// The tenant path segment the wizard's routes are gated by — this page is
// served at https://{slug}.{TENANT_BASE_DOMAIN}/setup/idp (ADR-010 §10), so
// the tenant is the subdomain, exactly like every other tenant-scoped route
// in this app.
const tenantSlug = tenantHost.split('.')[0]

type Step = 'token' | 'form' | 'done'

export default function SetupIdPPage() {
  const [step, setStep] = useState<Step>('token')
  const [token, setToken] = useState('')
  const [exchanging, setExchanging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [preset, setPreset] = useState<(typeof PRESET_KEYS)[number]>('custom')
  const [form, setForm] = useState<IdPFormState>(emptyIdPForm())

  const { redirectURI, postLogoutURI } = computeCallbackURIs(tenantHost)

  const handleExchange = async (e: React.FormEvent) => {
    e.preventDefault()
    setExchanging(true)
    setError(null)
    try {
      await apiPost(`/api/v1/setup/tenants/${tenantSlug}/exchange`, { token })
      setStep('form')
    } catch {
      // Deliberately generic — matches the backend's anti-enumeration
      // response (ADR-010 §10): never distinguish "wrong token" from
      // "this tenant/token doesn't exist".
      setError('Invalid or expired token. Contact your administrator for a new setup link.')
    } finally {
      setExchanging(false)
    }
  }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await apiPost(`/api/v1/setup/tenants/${tenantSlug}/idp`, buildIdPPayload(form))
      setStep('done')
    } catch (e: any) {
      setError(e.message ?? 'Save failed. Your setup session may have expired — refresh and try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-2xl">
        <div className="mb-8 flex items-center justify-center gap-2">
          <KeyRound className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-bold text-foreground">Identity Provider Setup</h1>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-danger/30 bg-danger-muted px-4 py-3 text-sm text-danger-foreground">
            {error}
          </div>
        )}

        {step === 'token' && (
          <Card className="p-8">
            <h2 className="mb-2 text-lg font-semibold text-foreground">Enter your setup token</h2>
            <p className="mb-6 text-sm text-muted">
              This token was issued when your workspace was provisioned. It's single-use for this
              setup session and expires on its own — nobody else can use it after you.
            </p>
            <form onSubmit={handleExchange} className="space-y-4">
              <FormField label="Setup token" htmlFor="setup-token">
                <Input
                  id="setup-token"
                  type="password"
                  required
                  autoFocus
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your setup token"
                />
              </FormField>
              <Button type="submit" className="w-full" disabled={exchanging}>
                {exchanging ? 'Verifying…' : 'Continue'}
              </Button>
            </form>
          </Card>
        )}

        {step === 'form' && (
          <Card className="p-8">
            <h2 className="mb-2 text-lg font-semibold text-foreground">Connect your identity provider</h2>
            <p className="mb-6 text-sm text-muted">
              Any OIDC-compliant provider works — Okta, Microsoft Entra ID, Keycloak, or your own.
              Sign-in method (password, MFA, passkeys) is entirely controlled by your provider's
              own policy, not configured here.
            </p>

            <div className="mb-6 space-y-2 rounded-lg border border-border bg-surface-muted px-4 py-3 text-xs text-muted">
              <p>
                Register these with your identity provider before saving:
              </p>
              <p>
                Redirect URI: <code className="text-foreground">{redirectURI}</code>
              </p>
              <p>
                Post-logout redirect URI: <code className="text-foreground">{postLogoutURI}</code>
              </p>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              <IdPFormFields
                form={form}
                setForm={setForm}
                editMode={false}
                preset={preset}
                onPresetChange={setPreset}
              />
              <Button type="submit" className="w-full" disabled={saving}>
                {saving ? 'Saving…' : 'Save and finish'}
              </Button>
            </form>
          </Card>
        )}

        {step === 'done' && (
          <Card className="p-8 text-center">
            <CheckCircle2 className="mx-auto mb-4 h-10 w-10 text-success" />
            <h2 className="mb-2 text-lg font-semibold text-foreground">Identity provider connected</h2>
            <p className="mb-6 text-sm text-muted">
              Your workspace is ready to sign in through your identity provider. This setup link
              will stop working automatically once an administrator signs in successfully — you
              don't need to do anything else.
            </p>
            <a href="/auth/login">
              <Button className="w-full">Go to sign-in</Button>
            </a>
          </Card>
        )}
      </div>
    </div>
  )
}
